import React, {useEffect, useMemo, useState, useCallback} from "react"
import {DataEmissionPanel} from "./components/data-emission/data-emission-panel"
import {Box} from "@mui/system"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import {Grid, Typography, Paper} from "@mui/material"
import theme from "../../theme/theme"
import DatalakeHeader from "./components/Header"
import EmissionTabsWrappedLabel from "./components/emission-tab/emission-tab"
import {ContrapartesPanel} from "./components/emission-contrapartes/contrapartes-panel"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import GenericModal from "src/v2/components/modal/generic-modal"
import {
    BoxCreditEmissionButtonGroupAdmin
} from "./components/box-admin-buttons-datalake-emission/box-admin-datalake-emission-admin-buttons"
import {EmissionSummarySections} from "./components/emission-sections/emission-summary-sections"
import {COLUMNS_MODAL_TABLE_CETIP_ISIN} from "./constants/full-list-cetip-isin.const"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"
import Loader from "src/v2/components/Loader/loader"
import {useCetipModal} from "./hooks/use-cetip-isin-modal"
import {TokenService} from "src/services/token-service"
import EmissionEmptyState from "./components/emission-empty-state/emission-empty-state"
import {BoxDataEventFlow} from "./components/box-data-event-flow/box-data-event-flow"
import {useEventFincsTable} from "./hooks/use-event-fincs-table"
import EventosFluxoService from "src/services/eventos_fluxo_service"
import SelectComponent from "src/v2/components/select/select-component"
import Documents from "./sub-page/Documents"
import RegisterDocumentService from "src/services/register-document-service"
import PropTypes from "prop-types"
import {MotorEmissaoService} from "../../../services/motor-emissao-service"
import EmissaoService from "../../../services/emissao-service"
import {useParameterMotor} from "./hooks/use-parameter-motor-price"

const SectionPaper = ({children, sx}) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: theme.palette.background.paper,
            borderRadius: 0,
            ...sx,
        }}
    >
        {children}
    </Paper>
)

SectionPaper.propTypes = {
    children: PropTypes.node.isRequired,
    sx: PropTypes.object,
}

const DataLakeEmissionV2 = () => {
    const emissaoMotorService = useMemo(() => new MotorEmissaoService(), [])
    const emissaoDatalakeService = useMemo(() => new EmissaoService(), [])


    const tokenService = useMemo(() => new TokenService(), [])
    const eventsFluxService = useMemo(() => new EventosFluxoService(), [])
    const documentService = useMemo(() => new RegisterDocumentService(), [])

    const isAdminUser = tokenService.verifyUserIsAdmin()

    const [characterData, setCharacterData] = useState({})
    const [contraparteData, setContraparteData] = useState({})

    const [isLoadingCharacters, setIsLoadingCharacters] = useState(false)
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)
    const [eventFlowSelectValue, setEventFlowSelectValue] = useState("")
    const [maxFlowEvent, setMaxFlowEvent] = useState(1)
    const [garantiasData, setGarantiasData] = useState({})
    const [offerData, setOfferData] = useState({})
    const [fatosRelevantesData, setFatosRelevantesData] = useState([])
    const pagination = {page: 0}

    const {
        state,
        handleOpenModal,
        handleRowClick,
        callBackOnChangeInputHeader,
        dispatch,
    } = useCetipModal(emissaoMotorService)

    const {state: parameterState, actions: parameterActions} = useParameterMotor(emissaoMotorService, state.rendaFixaCaracteristicasID)
    const {state: tableEventFluxState, actions: tableEventFluxActions} =
        useEventFincsTable(
            eventsFluxService,
            state.selectCetipIsin,
            eventFlowSelectValue
        )

    const rowFluxEvent = tableEventFluxState?.data?.data?.[`fluxoBase${eventFlowSelectValue}`]?.data

    const loadGarantias = useCallback(async (cetip) => {
        try {
            const response = await emissaoMotorService.getGarantias(cetip)

            setGarantiasData(
                response?.body?.data ||
                response?.body ||
                {}
            )
        } catch {
            setGarantiasData({})
        }
    }, [emissaoMotorService])

    const loadTipoOferta = useCallback(async (cetipIsin) => {
        try {
            const response = await emissaoMotorService.getTipoOferta(cetipIsin)

            setOfferData(
                response?.body?.data ||
                response?.body ||
                {}
            )
        } catch {
            setOfferData({})
        }
    }, [emissaoMotorService])

    const loadFatosRelevantes = useCallback(async (cetipIsin) => {
        try {
            const response = await emissaoMotorService.getFatosRelevantes(cetipIsin)

            const data = response?.body?.data || response?.body || []

            setFatosRelevantesData(Array.isArray(data) ? data : [])
        } catch {
            setFatosRelevantesData([])
        }
    }, [emissaoMotorService])

    const loadCharacters = useCallback(async (cetipIsin) => {
        if (!cetipIsin) return

        setIsLoadingCharacters(true)

        try {
            const request = await emissaoMotorService.getEmissaoDetalhe(cetipIsin)

            const caracteristicas = request?.body?.Caracteristicas || {}
            const contrapartes = request?.body?.Contrapartes || {}

            setCharacterData(caracteristicas)
            setContraparteData(contrapartes)

            Promise.allSettled([
                loadGarantias(cetipIsin),
                loadTipoOferta(cetipIsin),
                loadFatosRelevantes(cetipIsin),
            ])
        } catch {
            setCharacterData({})
            setContraparteData({})
            setGarantiasData({})
            setOfferData({})
            setFatosRelevantesData([])
        } finally {
            setIsLoadingCharacters(false)
        }
    }, [emissaoMotorService, loadGarantias, loadTipoOferta, loadFatosRelevantes])

    useEffect(() => {
        if (!state.selectCetipIsin) return

        const loadData = async () => {
            await Promise.allSettled([
                parameterActions.loadParametros(),
                loadCharacters(state.selectCetipIsin),
            ])
        }

        loadData()
    }, [state.selectCetipIsin])

    useEffect(() => {
        const selectedFlow = characterData?.VersaoDocumento ?? null
        if (selectedFlow === null) return

        setMaxFlowEvent(Number(selectedFlow + 1))
        setEventFlowSelectValue(selectedFlow + 1)
    }, [characterData?.VersaoDocumento])

    useEffect(() => {
        if (!state.selectCetipIsin || !eventFlowSelectValue) return

        tableEventFluxActions.loadData()
    }, [state.selectCetipIsin, eventFlowSelectValue])

    const isPageLoading =
        state.isLoadingModalData ||
        isLoadingCharacters ||
        isLoadingDocuments

    return (
        <SideBarLayout title="Emissão">
            {isPageLoading ? (
                <LoaderOverlay>
                    <Loader/>
                </LoaderOverlay>
            ) : (
                <GenericModal
                    isOpen={state.listFullModalVisible && !state.isLoadingModalData}
                    onClose={() => dispatch({type: "TOGGLE_MODAL"})}
                    title="Lista de CETIP/ISIN"
                    width="65%"
                >
                    <DataGridTable
                        columns={COLUMNS_MODAL_TABLE_CETIP_ISIN}
                        rows={state.rows}
                        pagination={pagination}
                        pageSize={25}
                        disableDeleteColumn
                        onRowClick={handleRowClick}
                        pointerCursor
                    />
                </GenericModal>
            )}

            <Grid
                sx={{
                    background: theme.palette.background.primary,
                    height: "100%",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: {
                            xxxs: 0,
                            lg: 209,
                        },
                        right: 0,
                        zIndex: 0,
                    }}
                >
                    <DatalakeHeader
                        emissaoService={emissaoMotorService}
                        callBackOnChange={callBackOnChangeInputHeader}
                        handleClickFullList={handleOpenModal}
                        externalValue={state.selectCetipIsin}
                    />
                </Box>

                {!state.selectCetipIsin && Object.keys(characterData).length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: {xxxs: 36, md: 30, lg: 22},
                            width: "100%",
                            background: theme.palette.background.paper,
                            flexDirection: "column",
                        }}
                    >
                        <EmissionEmptyState handleClickFullList={handleOpenModal}/>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: {xxxs: 36, md: 30, lg: 22},
                            width: "100%",
                            background: theme.palette.background.paper,
                            flexDirection: "column",
                        }}
                    >
                        <Box>
                            <EmissionTabsWrappedLabel
                                emissionTabChildren={
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            background: theme.palette.background.primary,
                                        }}
                                    >
                                        <SectionPaper>
                                            <DataEmissionPanel characterData={characterData}/>
                                        </SectionPaper>

                                        <SectionPaper sx={{mt: 3}}>
                                            <ContrapartesPanel contraparteData={contraparteData}/>

                                            {isAdminUser && (
                                                <SectionPaper sx={{mt: 1}}>
                                                    <BoxCreditEmissionButtonGroupAdmin
                                                        rendaFixaCaracteristicasID={state.rendaFixaCaracteristicasID}
                                                    />
                                                </SectionPaper>
                                            )}

                                            <SectionPaper sx={{mt: 1}}>
                                                <EmissionSummarySections
                                                    cetipIsin={state.selectCetipIsin}
                                                    MotorEmissaoService={emissaoMotorService}
                                                    emissaoDatalakeService={emissaoDatalakeService}
                                                    characterData={characterData}
                                                    contraparteData={contraparteData}
                                                    garantiasData={garantiasData}
                                                    offerData={offerData}
                                                    fatosRelevantesData={fatosRelevantesData}
                                                    admin={isAdminUser}
                                                />
                                            </SectionPaper>
                                        </SectionPaper>
                                    </Box>
                                }
                                eventFlowTabChildren={
                                    <Box
                                        sx={{
                                            height: "100%",
                                            width: {
                                                lg: "calc(100vw - 300px)",
                                            },
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 0,
                                            p: 3,
                                            pt: 0,
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <SectionPaper>
                                            <SelectComponent
                                                label={null}
                                                value={eventFlowSelectValue}
                                                onChange={(e) => setEventFlowSelectValue(e.target.value)}
                                                options={Array.from(
                                                    {length: maxFlowEvent || 1},
                                                    (_, i) => ({
                                                        value: String(i + 1),
                                                        label: `Fluxo ${i + 1}`,
                                                    })
                                                )}
                                            />
                                        </SectionPaper>

                                        <SectionPaper>
                                            <BoxDataEventFlow
                                                loading={parameterState?.loading?.parametros ?? false}
                                                parametersData={parameterState?.data?.parametros?.Parametros?.[0] ?? {}}
                                            />
                                        </SectionPaper>

                                        <SectionPaper>
                                            <Typography variant="h3" color="common.white">
                                                Eventos do fluxo
                                            </Typography>

                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    overflowX: "auto",
                                                    width: "100%",
                                                    display: "block",
                                                    mt: 1,
                                                }}
                                            >
                                                <DataGridTable
                                                    columns={tableEventFluxState.columns}
                                                    rows={rowFluxEvent ?? []}
                                                    disableDeleteColumn
                                                    getRowId={(row) => row.uuid}
                                                    loading={tableEventFluxState.loading}
                                                    resetKey={`fluxoBase${eventFlowSelectValue}`}
                                                />
                                            </Box>
                                        </SectionPaper>
                                    </Box>
                                }
                                documentsChildren={
                                    <Box
                                        sx={{
                                            height: "100vh",
                                            width: {
                                                lg: "calc(100vw - 300px)",
                                            },
                                            p: 3,
                                        }}
                                    >
                                        <SectionPaper>
                                            <Documents
                                                isAdmin={isAdminUser}
                                                documentService={documentService}
                                                cetip={state.selectCetipIsin}
                                                setIsLoading={setIsLoadingDocuments}
                                            />
                                        </SectionPaper>
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>
                )}
            </Grid>
        </SideBarLayout>
    )
}

export default DataLakeEmissionV2
